/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import s from "./Card.module.css";
import cn from "classnames";
import { FontAwesomeIcon, Loader, Stack } from "..";
import { FC, MouseEvent } from "react";

type ICardVariant = "pref" | "doc" | "downloadable" | "allergy";
type ICardState = "success" | "failed" | "loading";

type Props = {
  media?: Function;
  title: string;
  note?: string;
  variant?: ICardVariant;
  state: ICardState;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  onIconClick: (e: MouseEvent<HTMLDivElement>) => void;
};

const Card: FC<Props> = ({
  media,
  title,
  note,
  variant,
  state,
  isDisabled,
  isLoading,
  onClick,
  onIconClick,
}) => {
  let successState = state === "success" && !isDisabled;
  let failState = state === "failed" && !isDisabled;
  let loadingState = state === "loading" && !isDisabled;
  const disabledFlag = isDisabled || state === "loading";
  let svgFill;
  let svgStroke;
  let circleStroke;
  const handleIconClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onIconClick) onIconClick(e);
  };
  if (variant === "pref") {
    svgFill = "var(--component-colors-gd-option-selection-card-selected-high)";
    svgStroke = "var(--theme-colors-action-primary-clicked-fg)";
    circleStroke = "none";
  }
  return (
    <div
      role={disabledFlag ? "" : "button"}
      onClick={disabledFlag ? undefined : onClick && onClick}
    >
      <div
        className={cn(
          s.root,
          "-rounded-preferences-container",
          variant && s[variant],
          { [s.active]: successState }, // success
          { [s.loading]: loadingState }, // loading
          isDisabled && s.disabled
        )}
      >
        <div>
          {variant === "allergy" &&
            // If media is a component, pass `isActive` prop if the state is success
            (typeof media === "function"
              ? media({ isActive: successState })
              : null)}
        </div>
        <div
          className={cn(
            "relative",
            variant === "allergy" && "mt-1.5",
            variant === "allergy" && !media && "mt-auto mb-auto px-2.5"
          )}
        >
          <Stack className="text-start">
            <h5
              className={cn("capitalize", {
                "font-bold": successState,
              })}
            >
              {title}
            </h5>
            {note !== "" && <p>{note}</p>}
          </Stack>
          {/* {successState && variant === "doc" && (
            <svg
              width="39"
              height="39"
              viewBox="0 0 33 33"
              fill={svgFill}
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="16.5"
                cy="16.5"
                r="15.56"
                stroke={circleStroke}
                strokeWidth="1.88"
              />
              <path
                d="M9 17.9609L13.5962 22.5571L24.5563 11.597"
                stroke={svgStroke}
                strokeWidth="1.88"
              />
            </svg>
          )} */}
          {/* {successState && variant === "downloadable" && (
            <div
              className="h-24 justify-center items-center flex -mx-5 -mb-5 -mt-5 w-24"
              role="button"
              onClick={handleIconClick}
            >
              "sss"
              <svg
                width="39"
                height="39"
                viewBox="0 0 33 30"
                fill={svgFill}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.7868 19.994V21.6004H26.0166C29.8173 21.6004 32.0637 19.3017 32.0637 16.2456C32.0637 13.7249 30.5617 11.5829 27.9888 10.6687C28.0802 4.96124 24.0184 0.794922 18.8333 0.794922C15.2678 0.794922 12.8124 2.72789 11.3627 5.06573C8.39794 4.20373 4.75405 6.42403 4.74098 10.0287C1.8546 10.3814 0 12.68 0 15.6709C0 18.9883 2.40314 21.6004 6.53028 21.6004H12.1986V19.994H6.51722C3.33044 19.994 1.61951 18.0871 1.61951 15.6709C1.61951 13.1763 3.19984 11.2956 5.96868 11.2956C6.13847 11.2956 6.20377 11.2042 6.20377 11.0475C6.20377 7.20766 9.10321 5.94078 11.859 6.73748C12.0027 6.77666 12.0941 6.75054 12.1594 6.61993C13.4524 4.3474 15.4115 2.40137 18.8333 2.40137C23.1041 2.40137 26.1603 5.78406 26.4215 9.84589C26.4607 10.3814 26.4085 11.0213 26.3823 11.5829C26.3693 11.7397 26.4346 11.8311 26.5783 11.8572C28.9422 12.4058 30.4442 14.0122 30.4442 16.2456C30.4442 18.4136 28.9161 19.994 26.0166 19.994H19.7868ZM15.9992 29.267C16.1951 29.267 16.3649 29.1886 16.5739 29.0057L21.1581 24.6043C21.3279 24.4476 21.3932 24.2778 21.3932 24.0688C21.3932 23.664 21.0798 23.3636 20.6749 23.3636C20.479 23.3636 20.27 23.455 20.1133 23.5987L17.893 25.819L16.7306 27.0205L16.7959 25.1268V13.542C16.7959 13.111 16.4302 12.7584 15.9992 12.7584C15.5551 12.7584 15.1894 13.111 15.1894 13.542V25.1268L15.2547 27.0336L14.0793 25.819L11.859 23.5987C11.7153 23.455 11.4933 23.3636 11.2974 23.3636C10.8795 23.3636 10.5791 23.664 10.5791 24.0688C10.5791 24.2778 10.6574 24.4476 10.8141 24.6043L15.4115 29.0057C15.6204 29.1886 15.7902 29.267 15.9992 29.267Z"
                  fill={svgStroke}
                />
              </svg>
            </div>
          )} */}
          {successState && variant === "pref" && (
            <FontAwesomeIcon name="fa-circle-check" size="xl" />
          )}
          {!disabledFlag && isLoading && <Loader className="end-0.5 top-2" />}
        </div>
      </div>
    </div>
  );
};

export default Card;
